import type { Proforma } from '@horevia/contracts';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  Dialog,
  DialogContent,
  ErrorState,
  FormField,
  Input,
  NoPermissionState,
  Notice,
  PageHeader,
  Skeleton,
  Textarea,
  Toolbar,
  toast,
} from '@horevia/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowRight, Ban, Check, Download, History, Mail, Printer, Save, Send } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession } from '@/features/auth/session';
import {
  BillingDocumentPreview,
  BillingDocumentScroller,
} from '@/features/billing/BillingDocumentPreview';
import {
  BillingDraftEditor,
  newDraftLine,
  type BillingDraftState,
} from '@/features/billing/BillingDraftEditor';
import { proformaPreviewProps } from '@/features/billing/preview';
import {
  acceptProforma,
  billingKeys,
  billingSettingsQueryOptions,
  cancelProforma,
  convertProforma,
  issueProforma,
  proformaPdf,
  proformaQueryOptions,
  updateProforma,
} from '@/features/billing/queries';
import { proformaActions, proformaTone } from '@/features/billing/status';
import { computeBillingTotals } from '@/features/billing/totals';
import { optionalWithholding, taxRulesQueryOptions } from '@/features/tax/queries';
import { openDownloadedFile } from '@/lib/api';
import { errorMessage, errorTraceId } from '@/lib/errors';
import { formatAmount, formatDateShort } from '@/lib/format';

export const Route = createFileRoute('/_app/billing/proformas/$proformaId')({
  component: ProformaDetailPage,
});

type ActiveDialog = 'none' | 'issue' | 'accept' | 'cancel' | 'convert';

function draftFrom(proforma: Proforma): BillingDraftState {
  return {
    subject: proforma.subject ?? '',
    lines: proforma.lines.map((line) =>
      newDraftLine({
        kind: line.kind,
        label: line.label,
        description: line.description ?? '',
        quantity: line.quantity,
        unitPrice: line.unitPrice,
      }),
    ),
    discountType: proforma.totals.discountType,
    discountValue: proforma.totals.discountValue,
    applyWithholding: proforma.totals.withholdingApplied,
    notes: proforma.notes ?? '',
    validUntil: proforma.validUntil ?? '',
  };
}

function ProformaDetailPage() {
  const { proformaId } = Route.useParams();
  const { t, i18n } = useTranslation(['billing', 'common', 'errors']);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const session = useSession();
  const canRead = session.can('proforma.read');

  const query = useQuery(proformaQueryOptions(proformaId, canRead));
  const settings = useQuery(billingSettingsQueryOptions(canRead));
  const taxRules = useQuery(taxRulesQueryOptions({ kind: 'WITHHOLDING' }, canRead));
  const withholdingRule = optionalWithholding(taxRules.data);

  const [dialog, setDialog] = useState<ActiveDialog>('none');
  const [cancelReason, setCancelReason] = useState('');
  const [acceptedAt, setAcceptedAt] = useState('');
  const [draft, setDraft] = useState<BillingDraftState | null>(null);
  const [dirty, setDirty] = useState(false);

  const proforma = query.data;

  useEffect(() => {
    if (!proforma) return;
    setDraft(draftFrom(proforma));
    setDirty(false);
  }, [proforma?.id, proforma?.version]); // eslint-disable-line react-hooks/exhaustive-deps

  const liveTotals = useMemo(() => {
    if (!draft) return null;
    return computeBillingTotals({
      lines: draft.lines,
      discountType: draft.discountType,
      discountValue: draft.discountValue,
      applyWithholding: draft.applyWithholding,
      ...(withholdingRule ? { withholdingRate: withholdingRule.rate } : {}),
      locale: i18n.language,
    });
  }, [draft, withholdingRule, i18n.language]);

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: billingKeys.all });
  };

  const save = useMutation({
    mutationFn: () => {
      if (!proforma || !draft) throw new Error('missing');
      return updateProforma(proforma.id, {
        version: proforma.version,
        subject: draft.subject || null,
        lines: draft.lines.map((line) => ({
          kind: line.kind,
          label: line.label,
          description: line.description || null,
          quantity: line.quantity,
          unitPrice: line.unitPrice,
        })),
        discountType: draft.discountType,
        discountValue: draft.discountValue,
        applyWithholding: draft.applyWithholding,
        notes: draft.notes || null,
        ...(draft.validUntil ? { validUntil: draft.validUntil } : {}),
      });
    },
    onSuccess: async () => {
      await invalidate();
      setDirty(false);
      toast.success(t('billing:toasts.proformaUpdated'));
    },
  });

  const action = useMutation({
    mutationFn: async (kind: ActiveDialog) => {
      if (!proforma) throw new Error('missing');
      switch (kind) {
        case 'issue':
          return issueProforma(proforma.id, proforma.version);
        case 'accept':
          return acceptProforma(
            proforma.id,
            proforma.version,
            acceptedAt ? new Date(`${acceptedAt}T12:00:00.000Z`).toISOString() : undefined,
          );
        case 'cancel':
          return cancelProforma(proforma.id, proforma.version, cancelReason);
        case 'convert':
          return convertProforma(proforma.id, proforma.version);
        default:
          throw new Error('unsupported');
      }
    },
    onSuccess: async (result, kind) => {
      await invalidate();
      setDialog('none');
      setCancelReason('');
      if (kind === 'convert') {
        toast.success(t('billing:toasts.proformaConverted'));
        await navigate({
          to: '/billing/invoices/$invoiceId',
          params: { invoiceId: (result as { id: string }).id },
        });
        return;
      }
      if (kind === 'issue') {
        toast.success(
          t('billing:toasts.proformaIssued', { number: (result as Proforma).number ?? '' }),
        );
        return;
      }
      toast.success(
        kind === 'accept'
          ? t('billing:toasts.proformaAccepted')
          : t('billing:toasts.proformaCancelled'),
      );
    },
  });

  const openPdf = useMutation({
    mutationFn: () => proformaPdf(proformaId),
    onSuccess: (file) => {
      openDownloadedFile(file);
      toast.success(t('billing:toasts.pdfOpened'));
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  if (!canRead) {
    return (
      <Container>
        <NoPermissionState
          title={t('common:states.noPermissionTitle')}
          description={t('common:states.noPermissionDescription')}
        />
      </Container>
    );
  }

  if (query.isPending) {
    return (
      <Container>
        <div className="stack" aria-busy="true">
          <Skeleton variant="title" />
          <Skeleton height={80} />
          <Skeleton height={480} />
        </div>
      </Container>
    );
  }

  if (query.isError || !proforma) {
    return (
      <Container>
        <ErrorState
          title={t('common:states.errorTitle')}
          description={errorMessage(query.error, t)}
          traceId={errorTraceId(query.error)}
          retryLabel={t('common:actions.retry')}
          onRetry={() => void query.refetch()}
          traceCopyLabel={t('common:actions.copy')}
          traceCopiedLabel={t('common:actions.copied')}
        />
      </Container>
    );
  }

  const actions = proformaActions(proforma.status, proforma.convertedInvoiceId, session.can);
  const isDraft = proforma.status === 'DRAFT';
  const previewProps = proformaPreviewProps(proforma, settings.data);
  const preview =
    isDraft && draft && liveTotals
      ? {
          ...previewProps,
          subject: draft.subject || null,
          notes: draft.notes || null,
          validUntil: draft.validUntil || null,
          totals: liveTotals,
          lines: draft.lines.map((line, index) => ({
            id: line.key,
            position: index + 1,
            kind: line.kind,
            label: line.label,
            description: line.description || null,
            quantity: line.quantity,
            unit: null,
            unitPrice: line.unitPrice,
            amount: Math.round(line.quantity * line.unitPrice),
            taxRuleCodes: [],
          })),
        }
      : previewProps;

  const documentBlock = (
    <BillingDocumentScroller>
      <BillingDocumentPreview {...preview} />
    </BillingDocumentScroller>
  );

  return (
    <Container>
      <PageHeader
        title={proforma.number ?? t('billing:status.proforma.DRAFT')}
        subtitle={
          proforma.number
            ? t('billing:proformas.detailSubtitle', {
                number: proforma.number,
                customer: proforma.billingParty.displayName,
              })
            : t('billing:proformas.draftSubtitle', {
                customer: proforma.billingParty.displayName,
              })
        }
        breadcrumb={[
          { label: t('common:appName'), link: <Link to="/">{t('common:appName')}</Link> },
          {
            label: t('billing:proformas.title'),
            link: <Link to="/billing/proformas">{t('billing:proformas.title')}</Link>,
          },
          { label: proforma.number ?? t('billing:status.proforma.DRAFT') },
        ]}
        actions={
          <div className="row row--wrap" style={{ gap: 8 }}>
            <Badge tone={proformaTone(proforma.status)} size="lg" dot>
              {t(`billing:status.proforma.${proforma.status}`)}
            </Badge>
            {proforma.validUntil ? (
              <span className="t-13 muted">
                {t('billing:document.meta.validUntil')} :{' '}
                {formatDateShort(proforma.validUntil, i18n.language)}
              </span>
            ) : null}
          </div>
        }
      />

      <Card className="mb-4 no-print">
        <Toolbar style={{ borderBottom: 0, borderRadius: 'var(--r-md)' }}>
          <span className="strong display t-16">
            {formatAmount(proforma.totals.netTotal, i18n.language)}
          </span>
          <span className="spacer" />
          <Button variant="ghost" size="sm" onClick={() => window.print()}>
            <Printer className="ico ico-14" aria-hidden="true" />
            {t('billing:document.print')}
          </Button>
          {actions.downloadPdf ? (
            <Button
              variant="secondary"
              size="sm"
              loading={openPdf.isPending}
              onClick={() => openPdf.mutate()}
            >
              <Download className="ico ico-14" aria-hidden="true" />
              {t('billing:document.downloadPdf')}
            </Button>
          ) : null}
          <Button variant="secondary" size="sm" disabled title={t('billing:document.sendSoon')}>
            <Mail className="ico ico-14" aria-hidden="true" />
            {t('billing:document.sendEmail')}
          </Button>
          {actions.cancel ? (
            <Button variant="ghost" size="sm" onClick={() => setDialog('cancel')}>
              <Ban className="ico ico-14" aria-hidden="true" />
              {t('billing:actions.cancel')}
            </Button>
          ) : null}
          {actions.accept ? (
            <Button variant="success" size="sm" onClick={() => setDialog('accept')}>
              <Check className="ico ico-14" aria-hidden="true" />
              {t('billing:actions.accept')}
            </Button>
          ) : null}
          {actions.issue ? (
            <Button variant="primary" size="sm" onClick={() => setDialog('issue')}>
              <Send className="ico ico-14" aria-hidden="true" />
              {t('billing:actions.issueProforma')}
            </Button>
          ) : null}
          {actions.convert ? (
            <Button variant="primary" size="sm" onClick={() => setDialog('convert')}>
              <ArrowRight className="ico ico-14" aria-hidden="true" />
              {t('billing:actions.convert')}
            </Button>
          ) : null}
        </Toolbar>
      </Card>

      {proforma.convertedInvoiceId ? (
        <Notice
          tone="info"
          className="mb-4 no-print"
          actions={
            <Button variant="secondary" size="sm" asChild>
              <Link
                to="/billing/invoices/$invoiceId"
                params={{ invoiceId: proforma.convertedInvoiceId }}
              >
                {t('billing:proformas.seeInvoice')}
              </Link>
            </Button>
          }
        >
          {t('billing:proformas.convertedNotice')}
        </Notice>
      ) : null}

      {proforma.status === 'CANCELLED' && proforma.cancellationReason ? (
        <Notice tone="warning" className="mb-4 no-print">
          {proforma.cancellationReason}
        </Notice>
      ) : null}

      {isDraft && draft ? (
        <div className="grid grid--editor-preview">
          <div className="stack no-print">
            <Notice tone="info">{t('billing:document.previewLive')}</Notice>
            <BillingDraftEditor
              kind="PROFORMA"
              value={draft}
              onChange={(next) => {
                setDraft(next);
                setDirty(true);
              }}
              totals={liveTotals ?? proforma.totals}
              canApplyDiscount={session.can('invoice.apply_discount')}
              {...(withholdingRule ? { withholdingRate: withholdingRule.rate } : {})}
            />
            <div className="row" style={{ gap: 8 }}>
              <Button
                variant="primary"
                loading={save.isPending}
                disabled={!dirty}
                onClick={() => save.mutate()}
              >
                <Save className="ico ico-16" aria-hidden="true" />
                {t('billing:editor.save')}
              </Button>
              {dirty ? (
                <span className="t-13" style={{ color: 'var(--warning)' }}>
                  {t('billing:editor.unsaved')}
                </span>
              ) : null}
            </div>
            {save.isError ? (
              <Notice tone="danger" title={t('common:states.errorTitle')}>
                {errorMessage(save.error, t)}
              </Notice>
            ) : null}
          </div>
          <div>{documentBlock}</div>
        </div>
      ) : (
        <div className="grid grid--main-aside">
          <div>{documentBlock}</div>
          <aside className="stack sticky-aside no-print">
            <Card>
              <CardHeader
                title={t('billing:proformas.lifecycle.title')}
                icon={<History className="ico ico-16" aria-hidden="true" />}
                iconTone="blue"
              />
              <CardBody density="tight">
                <div className="row row--wrap" style={{ gap: 0 }}>
                  {(['draft', 'issued', 'accepted', 'converted'] as const).map((step, index) => {
                    const reached =
                      index === 0 ||
                      (index === 1 && proforma.status !== 'DRAFT') ||
                      (index === 2 &&
                        (proforma.status === 'ACCEPTED' || proforma.convertedInvoiceId !== null)) ||
                      (index === 3 && proforma.convertedInvoiceId !== null);
                    return (
                      <div className={`step ${reached ? 'is-done' : ''}`} key={step}>
                        <span className="step__num">{index + 1}</span>
                        <span className="step__label">
                          {t(`billing:proformas.lifecycle.${step}`)}
                        </span>
                      </div>
                    );
                  })}
                </div>
                {actions.convert ? (
                  <Notice tone="info" className="mt-3">
                    <span className="t-13">{t('billing:proformas.convertNotice')}</span>
                  </Notice>
                ) : null}
              </CardBody>
            </Card>
          </aside>
        </div>
      )}

      {/* ------------------------------ Dialogues ------------------------------ */}
      <Dialog open={dialog === 'issue'} onOpenChange={(open) => setDialog(open ? 'issue' : 'none')}>
        <DialogContent
          title={t('billing:dialogs.issueProformaTitle')}
          description={t('billing:dialogs.issueProformaBody', {
            year: new Date().getFullYear(),
          })}
          closeLabel={t('common:actions.close')}
          footer={
            <>
              <Button variant="ghost" onClick={() => setDialog('none')}>
                {t('common:actions.cancel')}
              </Button>
              <Button
                variant="primary"
                loading={action.isPending}
                onClick={() => action.mutate('issue')}
              >
                {t('billing:actions.issueProforma')}
              </Button>
            </>
          }
        >
          {action.isError ? <Notice tone="danger">{errorMessage(action.error, t)}</Notice> : null}
        </DialogContent>
      </Dialog>

      <Dialog
        open={dialog === 'accept'}
        onOpenChange={(open) => setDialog(open ? 'accept' : 'none')}
      >
        <DialogContent
          title={t('billing:dialogs.acceptTitle')}
          description={t('billing:dialogs.acceptBody')}
          closeLabel={t('common:actions.close')}
          footer={
            <>
              <Button variant="ghost" onClick={() => setDialog('none')}>
                {t('common:actions.cancel')}
              </Button>
              <Button
                variant="success"
                loading={action.isPending}
                onClick={() => action.mutate('accept')}
              >
                {t('billing:actions.accept')}
              </Button>
            </>
          }
        >
          <FormField label={t('billing:dialogs.acceptedAt')}>
            {(field) => (
              <Input
                id={field.id}
                type="date"
                value={acceptedAt}
                onChange={(event) => setAcceptedAt(event.target.value)}
              />
            )}
          </FormField>
          {action.isError ? (
            <Notice tone="danger" className="mt-3">
              {errorMessage(action.error, t)}
            </Notice>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog
        open={dialog === 'cancel'}
        onOpenChange={(open) => setDialog(open ? 'cancel' : 'none')}
      >
        <DialogContent
          title={t('billing:dialogs.cancelTitle')}
          description={t('billing:dialogs.cancelBody')}
          closeLabel={t('common:actions.close')}
          footer={
            <>
              <Button variant="ghost" onClick={() => setDialog('none')}>
                {t('common:actions.cancel')}
              </Button>
              <Button
                variant="danger"
                disabled={cancelReason.trim().length < 3}
                loading={action.isPending}
                onClick={() => action.mutate('cancel')}
              >
                {t('billing:actions.cancel')}
              </Button>
            </>
          }
        >
          <FormField label={t('billing:dialogs.cancelReason')} required>
            {(field) => (
              <Textarea
                id={field.id}
                rows={3}
                value={cancelReason}
                onChange={(event) => setCancelReason(event.target.value)}
                placeholder={t('billing:dialogs.cancelReasonPlaceholder')}
              />
            )}
          </FormField>
          {action.isError ? (
            <Notice tone="danger" className="mt-3">
              {errorMessage(action.error, t)}
            </Notice>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog
        open={dialog === 'convert'}
        onOpenChange={(open) => setDialog(open ? 'convert' : 'none')}
      >
        <DialogContent
          title={t('billing:dialogs.convertTitle')}
          description={t('billing:dialogs.convertBody')}
          closeLabel={t('common:actions.close')}
          footer={
            <>
              <Button variant="ghost" onClick={() => setDialog('none')}>
                {t('common:actions.cancel')}
              </Button>
              <Button
                variant="primary"
                loading={action.isPending}
                onClick={() => action.mutate('convert')}
              >
                {t('billing:actions.convert')}
              </Button>
            </>
          }
        >
          {action.isError ? <Notice tone="danger">{errorMessage(action.error, t)}</Notice> : null}
        </DialogContent>
      </Dialog>
    </Container>
  );
}
